/* @flow */
import idx from 'idx';
import * as React from 'react';
import Modal from 'react-modal';
import { graphql } from 'react-apollo';

import BoxBody from 'components/BoxBody';
import Button from 'components/Button';
import ChatSettingsActions from '../../actions/ChatSettingsActions';
import CurrentUser from 'auth/CurrentUser';
import DeleteAccountConfirmationDialog from 'settings/components/DeleteAccountConfirmationDialog';
import Dialog from 'components/Dialog';
import Divider from 'components/Divider';
import Feature from 'components/Feature';
import Group from 'entities/Group';
import Heading from 'components/Heading';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';
import List from 'components/List';
import ListItem from 'components/ListItem';
import LoadingIndicator from 'components/LoadingIndicator';
import MediaObject from 'components/MediaObject';
import ModalTrigger from 'components/ModalTrigger';
import ParentChildSection from './ParentChildSection';
import PIIConsentForm from '../PIIConsentForm';
import ProfileHeaderView from '../ProfileHeaderView';
import RoleSection from './RoleSection';
import Row from 'components/Row';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import UserPasswordForm from '../UserPasswordForm';
import UserPersonalInfoForm from '../UserPersonalInfoForm';
import UserPreferredLanguageForm from '../UserPreferredLanguageForm';
import UserSchools from '../UserSchools';
import waitForPromises from 'lib/waitForPromises';

import USER_PROFILE_QUERY from './queries/UserProfileQuery.gql';

import type { UserProfileQuery } from './queries/__generated__/UserProfileQuery';

import './UserProfileView.scss';

type Props = {
  router: Object,
  data: ReactApolloData<UserProfileQuery>,
};

type State = {
  showCannotDeleteAccountModal: boolean,
};

function SchoolsSection() {
  return (
    <div className="UserProfileView-section">
      <UserSchools />
      <Divider size="xxxxlarge" />
    </div>
  );
}

class UserProfileView extends React.Component<Props, State> {
  state: State = {
    showCannotDeleteAccountModal: false,
  };

  static routePrefetch = [
    () => {
      ChatSettingsActions.loadOfficeHours();
    },
  ];

  componentWillReceiveProps(nextProps: Object) {
    if (!this.props.data.error && nextProps.data.error) {
      ToastActions.showDefaultErrorToast();
      console.warn(nextProps);
      console.error('GraphQL error');
    }
  }

  handleDeletedAccount() {
    window.location = LinkConstants.MARKETING;
  }

  renderDeleteAccountDialog = () => {
    const deleteAccountDialogSubtext = CurrentUser.getUser().canCreateGroups()
      ? 'You will no longer have access to your classes or participants. This action can’t be undone.'
      : 'You will no longer have access to your classes. This action can’t be undone.';

    return (
      <DeleteAccountConfirmationDialog
        subtext={deleteAccountDialogSubtext}
        onDeleted={this.handleDeletedAccount}
        sourceEventKey="from_delete_account_button"
        title="Are you sure you want to delete your account?"
      />
    );
  };

  renderDeleteButton = () => {
    return (
      <ModalTrigger modal={this.renderDeleteAccountDialog()}>
        <Button
          color="lava"
          data-test="delete-account"
          level="secondary"
          size="xsmall"
          eventElementName="UserSettingsView-deleteAccountBtn">
          Delete account
        </Button>
      </ModalTrigger>
    );
  };

  renderCantDeleteAccountModal = (reasonsCannotCloseAccount: string[]) => {
    return (
      <Modal isOpen={this.state.showCannotDeleteAccountModal}>
        <Dialog
          onRequestHide={() => this.setState({ showCannotDeleteAccountModal: false })}
          size="small">
          <BoxBody>
            <Row>
              <Heading>
                You cannot delete your account if you are rostered in an organization
              </Heading>
            </Row>
            <Row>
              <Text color="thunder">
                Contact your administrator if you would like to be removed from the organization
              </Text>
              <List marker="bullet" markerPosition="inside">
                {reasonsCannotCloseAccount.map(reason => (
                  <ListItem>
                    <Text size="xsmall">{reason}</Text>
                  </ListItem>
                ))}
              </List>
            </Row>
            <Button
              onClick={() => {
                this.setState({ showCannotDeleteAccountModal: false });
              }}
              full>
              Got it
            </Button>
          </BoxBody>
        </Dialog>
      </Modal>
    );
  };

  renderCannotDeleteAccountButton = (reasons: string[]) => {
    return (
      <React.Fragment>
        <MediaObject center>
          <Button color="lava" data-test="cannot-delete-account" disabled level="secondary">
            Delete account
          </Button>
          <Link
            onClick={() => {
              this.setState({ showCannotDeleteAccountModal: true });
            }}>
            Learn more
          </Link>
        </MediaObject>
        {this.renderCantDeleteAccountModal(reasons)}
      </React.Fragment>
    );
  };

  conditionallyRenderDeleteButton = () => {
    const reasons = CurrentUser.getUser().cannotCloseAccountReasons();
    return reasons.length > 0
      ? this.renderCannotDeleteAccountButton(reasons)
      : this.renderDeleteButton();
  };

  navToGroup = (group: Group) => {
    this.props.router.push(group.webUrl());
  };

  render() {
    const { data } = this.props;
    const isOrgPickerEligible = idx(data.me, _ => _.organization_picker_eligible);

    if (data.loading || data.error) {
      return <LoadingIndicator />;
    }

    return (
      <div className="UserProfileView">
        <Row>
          <ProfileHeaderView />
        </Row>
        <UserPersonalInfoForm />
        <Divider />
        <UserPasswordForm />
        <Divider />
        <Feature name="2020.dec.preferred_language_translation_v0">
          <UserPreferredLanguageForm preferredLanguage={data.me?.preferences?.preferred_language} />
        </Feature>
        <ParentChildSection />
        <RoleSection />
        <Divider />
        {isOrgPickerEligible && <SchoolsSection />}
        <PIIConsentForm />
        <Divider />
        <Row>{this.conditionallyRenderDeleteButton()}</Row>
      </div>
    );
  }
}

const withData = graphql(USER_PROFILE_QUERY);

export { UserProfileView };
export default waitForPromises(withData(UserProfileView));
